import React from 'react';

import { css } from '@emotion/css';
import { Typography } from 'antd';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';

import { KeyboardShortcut } from './KeyboardShortcut';

export type DailyWorkflowCardBaseProps = {
  title: string;
  headerActions: React.ReactNode;
  children: React.ReactNode;
  flexGrow?: number;
  noPadding?: boolean;
  keyboardShortcut?: string;
  onKeyboardShortcutClick?: () => void;
};

export const DailyWorkflowCardBase = ({
  title,
  headerActions,
  children,
  flexGrow,
  noPadding,
  keyboardShortcut,
  onKeyboardShortcutClick,
}: DailyWorkflowCardBaseProps) => (
  <div
    className={css`
      display: flex;
      border: 1px solid ${theme.colors.neutral[300]};
      border-radius: 8px;
      height: 100%;
      ${flexGrow ? `flex: ${flexGrow} 1 auto` : 'width: 100%'};
      min-width: 0;
    `}
  >
    <FlexSpace
      direction="column"
      gap={4}
      className={css`
        flex: 1 1 0;
        padding: ${noPadding ? '0px' : '6px'};
        padding-left: ${noPadding ? '0px' : '10px'};
        padding-right: ${noPadding ? '0px' : '10px'};
      `}
    >
      <FlexSpace
        direction="row"
        // In order to have a border on the bottom of the card, we need to add a pseudo-element that is a line
        // that is the same width as the card and is positioned absolutely at the bottom of the card.  This fixes an alignment issue we had with the border.
        className={css`
          width: 100%;
          position: relative;
          ${noPadding ? 'padding-top: 6px; padding-left: 10px; padding-right: 10px;' : ''}
          padding-bottom: 6px;
          &::after {
            content: '';
            position: absolute;
            left: ${noPadding ? '10px' : '0'};
            right: ${noPadding ? '10px' : '0'};
            bottom: 0;
            height: 1px;
            background: ${theme.colors.neutral[300]};
            pointer-events: none;
          }
        `}
        gap={10}
      >
        <Typography.Text style={{ fontSize: 12, fontWeight: 700 }}>{title}</Typography.Text>
        <FlexSpacer />
        {keyboardShortcut && onKeyboardShortcutClick && (
          <KeyboardShortcut shortcut={keyboardShortcut} onClick={onKeyboardShortcutClick} />
        )}
        {headerActions}
      </FlexSpace>
      {children}
    </FlexSpace>
  </div>
);
